import React, { useState, useEffect, useRef } from "react";
import Draggable from "react-draggable";
import "./ActiveFiresPanel.css";
import CloseIcon from "@mui/icons-material/Close";
import FilterNoneOutlinedIcon from "@mui/icons-material/FilterNoneOutlined";
import CropSquareOutlinedIcon from "@mui/icons-material/CropSquareOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import Popover from "@mui/material/Popover";
import { getLocalNowMinusOneHour } from "../../../utils/DateTimeUtils";
import {
  dateToIso,
  isToday,
  getLocalNowTimeStr,
} from "../../../utils/activeFiresDateUtils";
import ActiveFiresContent from "../../molecules/activeFiresContent/ActiveFiresContent";
import { useTranslation } from "react-i18next";

const ActiveFiresPanel = ({
  showComponent,
  handleDraggableComponentToggle,
  isLayerActive,
  onToggleLayer,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onApply,
  detectedCount,
  isLoading,
  isSmallScreen,
  onShowNavbarMenu,
  reducedDragWindow = false,
  onReduceDragWindow,
  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,
  timelineHours,
  timelineCursor,
  onTimelineCursorChange,
  timelineBuckets,
  selectedPreset,
  onChangePreset,
  displayStartDate,
  onChangeDisplayStartDate,
  displayEndDate,
  onChangeDisplayEndDate,
  onCumulativeChange,
}) => {
  const { t } = useTranslation();
  const [activeDrags, setActiveDrags] = useState(0);
  const [deltaPosition, setDeltaPosition] = useState({ x: 0, y: 0 });
 
  /* ---------- info popover (hover, same logic as LayerInfoIcon) ---------- */
  const [infoAnchorEl, setInfoAnchorEl] = useState(null);
  const infoOpen = Boolean(infoAnchorEl);
  const infoCloseTimer = useRef(null);

  const openInfo = (e) => {
    clearTimeout(infoCloseTimer.current);
    setInfoAnchorEl(e.currentTarget);
  };

  // Delayed close so the cursor can travel from the icon into the popover
  // (keeps the source link clickable). Cancelled by enter on either element.
  const scheduleCloseInfo = () => {
    clearTimeout(infoCloseTimer.current);
    infoCloseTimer.current = setTimeout(() => setInfoAnchorEl(null), 150);
  };

  useEffect(() => {
    return () => clearTimeout(infoCloseTimer.current);
  }, []);

  /* ----------------------------------------------------------------------- */

  // resolve end time: live current time if end date is today, else end-of-day
  const resolveEndTime = (endDateIso) =>
    isToday(endDateIso) ? getLocalNowTimeStr() : "23:59";
  const toInputDate = (val) => {
    if (!val) return "";
    const d = new Date(val);
    return isNaN(d.getTime()) ? "" : d.toISOString().split("T")[0];
  };
  const toInputTime = (date) => {
    if (!date) return "";
    const h = String(date.getHours()).padStart(2, "0");
    const m = String(date.getMinutes()).padStart(2, "0");
    return `${h}:${m}`;
  };

  // Custom range: applied as soon as BOTH dates are set (the content
  // component auto-fills the other date when only one is picked).
  useEffect(() => {
    if (displayStartDate || displayEndDate) {
      onChangePreset("perso");

      if (displayStartDate && displayEndDate) {
        const customStartTime = "00:00";
        const customEndTime = resolveEndTime(displayEndDate);

        onStartTimeChange(customStartTime);
        onEndTimeChange(customEndTime);

        if (!isLayerActive) onToggleLayer();
        onApply({
          startDate: displayStartDate,
          endDate: displayEndDate,
          startTime: customStartTime,
          endTime: customEndTime,
        });
      }
    } else if (
      selectedPreset === "perso" &&
      !displayStartDate &&
      !displayEndDate
    ) {
      onChangePreset("maintenant");
      applyMaintenant();
      if (isLayerActive) {
        onToggleLayer();
      }
    }
  }, [displayStartDate, displayEndDate]);

  const applyMaintenant = () => {
    const { date, time } = getLocalNowMinusOneHour();
    onStartDateChange(date);
    onStartTimeChange(time);
    onEndDateChange("");
    onEndTimeChange("");
    return { date, time };
  };

  const handlePreset = (preset) => {
    if (preset === "maintenant") {
      const { date, time } = applyMaintenant();
      onChangeDisplayStartDate("");
      onChangeDisplayEndDate("");
      onChangePreset("maintenant");
      if (!isLayerActive) onToggleLayer();
      onApply({ startDate: date, endDate: "", startTime: time, endTime: "" });
      return;
    }

    if (preset === "perso") {
      // Just reveals the two date inputs; the range is applied once both
      // dates are set (see effect above).
      onChangePreset("perso");
      return;
    }

    const end = new Date();
    const start = new Date();
    if (preset === "24h") start.setDate(end.getDate() - 1);
    else if (preset === "72h") start.setDate(end.getDate() - 3);
    else if (preset === "7jours") start.setDate(end.getDate() - 7);

    const now = toInputTime(end);
    const startDateStr = toInputDate(start);
    const endDateStr = toInputDate(end);

    onStartDateChange(startDateStr);
    onStartTimeChange(now);
    onEndDateChange(endDateStr);
    onEndTimeChange(now);

    onChangeDisplayStartDate("");
    onChangeDisplayEndDate("");
    onChangePreset(preset);

    if (!isLayerActive) onToggleLayer();
    onApply({
      startDate: startDateStr,
      endDate: endDateStr,
      startTime: now,
      endTime: now,
    });
  };

  // Called by the content component with Date | null for each input.
  const handleCustomRangeChange = (start, end) => {
    const startIso = dateToIso(start);
    const endIso = dateToIso(end);

    onStartTimeChange(start ? "00:00" : "");
    onEndTimeChange(end ? resolveEndTime(endIso) : "");

    onChangeDisplayStartDate(startIso);
    onChangeDisplayEndDate(endIso);

    onStartDateChange(startIso);
    onEndDateChange(endIso);
  };

  const handleStart = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setActiveDrags((prev) => prev + 1);
  };

  const handleDrag = (e, data) => {
    e.stopPropagation();
    e.preventDefault();
    setDeltaPosition({
      x: deltaPosition.x + data.deltaX,
      y: deltaPosition.y + data.deltaY,
    });
  };

  const handleStop = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setActiveDrags((prev) => prev - 1);
  };

  const returnToMenu = () => {
    handleDraggableComponentToggle();
    onShowNavbarMenu();
  };

  if (!showComponent) return null;

  const visibilityLabel = isLayerActive
    ? t("activeFires.buttons.hide")
    : t("activeFires.buttons.show", { defaultValue: "Show" });

  // Eye (show/hide layer) + info (data source) — shared by mobile and desktop.
  const layerActions = (
    <>
      {isLayerActive ? (
        <VisibilityOutlinedIcon
          onClick={onToggleLayer}
          className="drag-comp-closeIcon"
          style={{ fontSize: "22px" }}
          titleAccess={visibilityLabel}
          data-testid="active-fires-visibility-btn"
        />
      ) : (
        <VisibilityOffOutlinedIcon
          onClick={onToggleLayer}
          className="drag-comp-closeIcon"
          style={{ fontSize: "22px" }}
          titleAccess={visibilityLabel}
          data-testid="active-fires-visibility-btn"
        />
      )}
      <InfoOutlinedIcon
        onMouseEnter={openInfo}
        onMouseLeave={scheduleCloseInfo}
        className="drag-comp-closeIcon"
        style={{ fontSize: "20px", opacity: infoOpen ? 1 : undefined }}
        data-testid="active-fires-info-btn"
      />
      <Popover
        className="layer-info-popover"
        open={infoOpen}
        anchorEl={infoAnchorEl}
        onClose={() => setInfoAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        disableRestoreFocus
        disableScrollLock
      >
        <div
          className="layer-info-content"
          data-testid="active-fires-info-popover"
          onMouseEnter={() => clearTimeout(infoCloseTimer.current)}
          onMouseLeave={scheduleCloseInfo}
        >
          {t("activeFires.attribution.source")}{" "}
          <a
            href="https://lsa-saf.eumetsat.int/en/data/data-access/"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("activeFires.attribution.linkText")}
          </a>
        </div>
      </Popover>
    </>
  );

  const contentProps = {
    displayStartDate,
    displayEndDate,
    onRangeChange: handleCustomRangeChange,
    detectedCount,
    isLoading,
    selectedPreset,
    onPreset: handlePreset,
    isSmallScreen,
    isLayerActive,
    timelineHours,
    timelineCursor,
    onTimelineCursorChange,
    timelineBuckets,
    onToggleLayer,
    onCumulativeChange,
  };

  /* ---------- Mobile sidebar ---------- */
  if (isSmallScreen) {
    return (
      <div className="sidebar-draggableComp active-fires-sidebar open">
        <div className="drag-comp-header">
          <KeyboardDoubleArrowLeftIcon
            onClick={returnToMenu}
            className="drag-comp-returnIcon"
            data-testid="active-fires-return-btn"
          />
          <div className="drag-comp-title" data-testid="active-fires-title">
            {t("activeFires.title")}
          </div>
          <div className="drag-header-icons">
            {layerActions}
            <CloseIcon
              onClick={handleDraggableComponentToggle}
              className="drag-comp-closeIcon"
              data-testid="active-fires-close-btn"
            />
          </div>
        </div>
        <div className="drag-comp-body" data-testid="active-fires-body">
          <ActiveFiresContent {...contentProps} />
        </div>
      </div>
    );
  }

  /* ---------- Desktop draggable ---------- */
  return (
    <Draggable
      handle=".handle"
      defaultPosition={{ x: -50, y: 50 }}
      position={null}
      grid={[25, 25]}
      scale={1}
      onStart={handleStart}
      onDrag={handleDrag}
      onStop={handleStop}
    >
      <div
        className="draggable-comp active-fires-comp"
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        data-testid="active-fires-panel"
      >
        <div className="drag-comp-header handle">
          <div className="drag-comp-title" data-testid="active-fires-title">
            {t("activeFires.title")}
          </div>
          <div className="drag-header-icons">
            {layerActions}
            {reducedDragWindow ? (
              <CropSquareOutlinedIcon
                onClick={onReduceDragWindow}
                className="drag-comp-closeIcon"
                style={{ fontSize: "20px" }}
                data-testid="active-fires-expand-btn"
              />
            ) : (
              <FilterNoneOutlinedIcon
                onClick={onReduceDragWindow}
                className="drag-comp-closeIcon"
                style={{ fontSize: "17px" }}
                data-testid="active-fires-reduce-btn"
              />
            )}
            <CloseIcon
              onClick={handleDraggableComponentToggle}
              className="drag-comp-closeIcon"
              style={{ fontSize: "20px" }}
              data-testid="active-fires-close-btn"
            />
          </div>
        </div>

        {!reducedDragWindow && (
          <div className="drag-comp-body" data-testid="active-fires-body">
            <ActiveFiresContent {...contentProps} />
          </div>
        )}
      </div>
    </Draggable>
  );
};

export default ActiveFiresPanel;
